// src/components/game/PuzzleGame.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { JigsawPuzzle } from 'react-jigsaw-puzzle';
import 'react-jigsaw-puzzle/lib/jigsaw-puzzle.css';
import { useScore } from '../../context/ScoreContext';

const PuzzleGame = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [isSolved, setIsSolved] = useState(false);
  // ✅ attempts - გამოყენებულია handleImageChange და handleDifficultyChange-ში
  const [attempts, setAttempts] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [rows, setRows] = useState(4);
  const [cols, setCols] = useState(4);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    const saved = getScore('puzzle');
    setBestScore(saved || 0);
  }, [getScore]);

  // მრავალი სურათის მასივი
  const images = [
    { id: 1, src: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600', title: '💻 კოდი' },
    { id: 2, src: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600', title: '🌆 ქალაქი' },
    { id: 3, src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600', title: '🌌 ვარსკვლავები' },
    { id: 4, src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600', title: '🏞️ ბუნება' },
    { id: 5, src: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=600', title: '🌊 ზღვა' },
    { id: 6, src: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600', title: '🌅 მზის ჩასვლა' },
    { id: 7, src: 'https://images.unsplash.com/photo-1470071459604-7b3ec3c2fe86?w=600', title: '🌿 ტყე' },
    { id: 8, src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600', title: '🌲 ბუნება' },
    { id: 9, src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600', title: '🏖️ პლაჟი' },
  ];

  useEffect(() => {
    let timer;
    if (isPlaying && !isSolved) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, isSolved]);

  const handleSolved = () => {
    if (!isSolved) {
      setIsSolved(true);
      setIsPlaying(false);
      setShowCelebration(true);
      
      const timeBonus = time < 30 ? 5 : time < 60 ? 3 : time < 120 ? 1 : 0;
      const difficultyBonus = rows * cols > 20 ? 3 : rows * cols > 15 ? 2 : 1;
      const newScore = 1 + timeBonus + difficultyBonus;
      
      if (newScore > bestScore) {
        updateScore('puzzle', newScore);
        setBestScore(getScore('puzzle'));
      } else {
        updateScore('puzzle', newScore);
        setBestScore(getScore('puzzle'));
      }

      setTimeout(() => {
        setShowCelebration(false);
      }, 3000);
    }
  };

  const handleImageChange = (index) => {
    setCurrentImage(index);
    setIsSolved(false);
    setAttempts(prev => prev + 1); // ✅ attempts გამოყენება
    setTime(0);
    setIsPlaying(true);
    setShowCelebration(false);
  };

  const handleDifficultyChange = (size) => {
    setRows(size);
    setCols(size);
    setIsSolved(false);
    setAttempts(prev => prev + 1); // ✅ attempts გამოყენება
    setTime(0);
    setIsPlaying(true);
    setShowCelebration(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: 'clamp(12px, 3vw, 20px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: 'min(750px, 95vw)',
    width: '100%',
    padding: 'clamp(15px, 4vw, 30px)',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    borderRadius: 'clamp(16px, 2vw, 20px)',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  const puzzleContainerStyle = {
    width: 'clamp(260px, 75vw, 500px)',
    height: 'clamp(260px, 75vw, 500px)',
    margin: '0 auto',
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: isDarkMode 
      ? '0 8px 32px rgba(0,0,0,0.4)' 
      : '0 8px 32px rgba(0,0,0,0.1)'
  };

  const buttonStyle = (active) => ({
    padding: 'clamp(6px, 1vw, 8px) clamp(12px, 1.5vw, 16px)',
    margin: '0 clamp(3px, 0.5vw, 5px)',
    border: '2px solid',
    borderColor: active ? '#667eea' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
    background: active ? '#667eea' : 'transparent',
    color: active ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
    borderRadius: 'clamp(8px, 1vw, 10px)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: 'clamp(11px, 1.2vw, 14px)',
    fontWeight: active ? 'bold' : 'normal',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent'
  });

  const imageButtonStyle = (active) => ({
    padding: 'clamp(4px, 0.8vw, 6px) clamp(8px, 1.2vw, 12px)',
    margin: 'clamp(2px, 0.5vw, 4px)',
    border: '2px solid',
    borderColor: active ? '#667eea' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
    background: active ? '#667eea' : 'transparent',
    color: active ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: 'clamp(9px, 1vw, 12px)',
    whiteSpace: 'nowrap',
    fontWeight: active ? 'bold' : 'normal',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent'
  });

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes confetti {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(-100px) rotate(720deg); opacity: 0; }
          }
          .game-title {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .celebration {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 80px;
            z-index: 1000;
            animation: confetti 2s ease-out forwards;
            pointer-events: none;
          }
          @media (max-width: 480px) {
            .celebration {
              font-size: 50px;
            }
          }
        `}
      </style>

      <Link to="/games" style={{
        display: 'inline-block',
        padding: 'clamp(10px, 1.5vw, 12px) clamp(20px, 3vw, 28px)',
        margin: 'clamp(8px, 1vw, 10px) 0',
        background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        color: isDarkMode ? '#fff' : '#000',
        textDecoration: 'none',
        borderRadius: 'clamp(10px, 1.5vw, 12px)',
        transition: 'all 0.3s',
        backdropFilter: 'blur(10px)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
        fontWeight: '500',
        fontSize: 'clamp(13px, 1.5vw, 16px)'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateX(-5px)';
        e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateX(0)';
        e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
      }}>
        ← თამაშებში დაბრუნება
      </Link>
      
      {showCelebration && (
        <div className="celebration">🎉</div>
      )}

      <div style={containerStyle}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          flexWrap: 'wrap',
          gap: 'clamp(8px, 1vw, 10px)'
        }}>
          <h1 style={{ 
            margin: 0,
            fontSize: 'clamp(20px, 3vw, 32px)'
          }}>
            <span className="game-title">🧩 Jigsaw</span>
          </h1>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'clamp(10px, 1.5vw, 15px)',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 'clamp(13px, 1.5vw, 16px)' }}>
              🏆 {bestScore}
            </span>
            {isPlaying && !isSolved && (
              <span style={{ color: isDarkMode ? '#aaa' : '#666', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
                ⏱️ {formatTime(time)}
              </span>
            )}
            {isSolved && (
              <span style={{ 
                color: '#4ade80',
                fontWeight: 'bold',
                animation: 'pulse 1s infinite',
                fontSize: 'clamp(13px, 1.5vw, 15px)'
              }}>
                ✅ ამოხსნილია!
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(65px, 1fr))',
          gap: 'clamp(6px, 1vw, 8px)',
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          padding: 'clamp(10px, 1.5vw, 12px)',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: 'clamp(10px, 1.5vw, 12px)',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>🖼️</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {currentImage + 1}/{images.length}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>სურათი</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>📐</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {rows}x{cols}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>ზომა</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>🧩</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {rows * cols}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>ნაწილი</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>⏱️</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {formatTime(time)}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
          </div>
        </div>

        {/* სურათების არჩევა - Mobile Friendly */}
        <div style={{ 
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'clamp(2px, 0.5vw, 4px)',
          justifyContent: 'center'
        }}>
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => handleImageChange(idx)}
              style={imageButtonStyle(currentImage === idx)}
              title={img.title}
            >
              {img.title}
            </button>
          ))}
        </div>

        {/* სირთულის არჩევა - Mobile Friendly */}
        <div style={{ 
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          display: 'flex',
          gap: 'clamp(3px, 0.5vw, 5px)',
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <span style={{ 
            color: isDarkMode ? '#aaa' : '#666',
            marginRight: 'clamp(6px, 1vw, 10px)',
            fontSize: 'clamp(11px, 1.2vw, 14px)'
          }}>
            📐 სირთულე:
          </span>
          {[3, 4, 5, 6].map(size => (
            <button
              key={size}
              onClick={() => handleDifficultyChange(size)}
              style={buttonStyle(rows === size && cols === size)}
            >
              {size}x{size}
            </button>
          ))}
        </div>

        {/* თავსატეხი */}
        <div style={puzzleContainerStyle}>
          <JigsawPuzzle
            key={`${currentImage}-${rows}-${cols}`}
            imageSrc={images[currentImage].src}
            rows={rows}
            columns={cols}
            onSolved={handleSolved}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>

        {/* ინფორმაცია */}
        <div style={{
          marginTop: 'clamp(15px, 2vw, 20px)',
          padding: 'clamp(12px, 1.5vw, 15px)',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
          borderRadius: 'clamp(10px, 1.5vw, 12px)',
          textAlign: 'center',
          color: isDarkMode ? '#aaa' : '#666',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          <p style={{ margin: '5px 0', fontSize: 'clamp(11px, 1.2vw, 14px)' }}>
            🖱️ გადაიტანეთ ნაწილები ადგილზე
          </p>
          <p style={{ margin: '5px 0', fontSize: 'clamp(10px, 1vw, 13px)', color: isDarkMode ? '#666' : '#999' }}>
            {rows}x{cols} - {rows * cols} ნაწილი
            {isSolved && ` • 🎯 ${bestScore} ქულა`}
          </p>
        </div>

        {/* Progress */}
        {!isSolved && isPlaying && (
          <div style={{
            marginTop: 'clamp(8px, 1vw, 10px)',
            padding: 'clamp(6px, 0.8vw, 8px)',
            background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
            borderRadius: 'clamp(6px, 0.8vw, 8px)',
            textAlign: 'center'
          }}>
            <span style={{ 
              fontSize: 'clamp(10px, 1vw, 12px)', 
              color: isDarkMode ? '#666' : '#999'
            }}>
              💪 ააწყვეთ სურათი!
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PuzzleGame;